import type { PackRecord, ProfileRecord } from '../types';
import { incrementPackDownload, incrementProfileDownload } from './downloadCounts';

interface ZipEntry {
  name: string;
  bytes: Uint8Array;
  date: Date;
}

let crcTable: Uint32Array | null = null;

function makeCrcTable(): Uint32Array {
  const table = new Uint32Array(256);
  for (let index = 0; index < 256; index += 1) {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    table[index] = value >>> 0;
  }
  return table;
}

export function crc32(bytes: Uint8Array): number {
  crcTable ??= makeCrcTable();
  let crc = 0xffffffff;
  for (const byte of bytes) crc = crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

function dosDateTime(date: Date): [number, number] {
  const safe = Number.isNaN(date.getTime()) || date.getFullYear() < 1980 ? new Date('1980-01-01T00:00:00Z') : date;
  const time = (safe.getHours() << 11) | (safe.getMinutes() << 5) | Math.floor(safe.getSeconds() / 2);
  const day = Math.max(1, safe.getDate());
  const encodedDate = ((safe.getFullYear() - 1980) << 9) | ((safe.getMonth() + 1) << 5) | day;
  return [time, encodedDate];
}

function concat(parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const output = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    output.set(part, offset);
    offset += part.length;
  }
  return output;
}

export function createStoredZip(entries: ZipEntry[]): Uint8Array {
  const encoder = new TextEncoder();
  const localParts: Uint8Array[] = [];
  const centralParts: Uint8Array[] = [];
  let localOffset = 0;

  for (const entry of entries) {
    const name = encoder.encode(entry.name.replaceAll('\\', '/'));
    const crc = crc32(entry.bytes);
    const [time, date] = dosDateTime(entry.date);
    const local = new Uint8Array(30 + name.length);
    const localView = new DataView(local.buffer);
    localView.setUint32(0, 0x04034b50, true);
    localView.setUint16(4, 20, true);
    localView.setUint16(6, 0x0800, true);
    localView.setUint16(8, 0, true);
    localView.setUint16(10, time, true);
    localView.setUint16(12, date, true);
    localView.setUint32(14, crc, true);
    localView.setUint32(18, entry.bytes.length, true);
    localView.setUint32(22, entry.bytes.length, true);
    localView.setUint16(26, name.length, true);
    local.set(name, 30);
    localParts.push(local, entry.bytes);

    const central = new Uint8Array(46 + name.length);
    const centralView = new DataView(central.buffer);
    centralView.setUint32(0, 0x02014b50, true);
    centralView.setUint16(4, 20, true);
    centralView.setUint16(6, 20, true);
    centralView.setUint16(8, 0x0800, true);
    centralView.setUint16(10, 0, true);
    centralView.setUint16(12, time, true);
    centralView.setUint16(14, date, true);
    centralView.setUint32(16, crc, true);
    centralView.setUint32(20, entry.bytes.length, true);
    centralView.setUint32(24, entry.bytes.length, true);
    centralView.setUint16(28, name.length, true);
    centralView.setUint32(38, 0, true);
    centralView.setUint32(42, localOffset, true);
    central.set(name, 46);
    centralParts.push(central);
    localOffset += local.length + entry.bytes.length;
  }

  const centralDirectory = concat(centralParts);
  const end = new Uint8Array(22);
  const endView = new DataView(end.buffer);
  endView.setUint32(0, 0x06054b50, true);
  endView.setUint16(8, entries.length, true);
  endView.setUint16(10, entries.length, true);
  endView.setUint32(12, centralDirectory.length, true);
  endView.setUint32(16, localOffset, true);
  return concat([...localParts, centralDirectory, end]);
}

function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function downloadProfile(profile: ProfileRecord): Promise<void> {
  const response = await fetch(profile.fileUrl);
  if (!response.ok) throw new Error(`Could not download ${profile.fileName} (${response.status}).`);
  const bytes = new Uint8Array(await response.arrayBuffer());
  saveBlob(new Blob([bytes as BlobPart], { type: 'application/octet-stream' }), profile.fileName);
  incrementProfileDownload(profile.id);
}

function safeArchiveName(value: string): string {
  return value.replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim();
}

function twoDigits(value: number): string {
  return String(value).padStart(2, '0');
}

export function selectedProfilesArchiveName(date = new Date()): string {
  const datePart = `${twoDigits(date.getMonth() + 1)}${twoDigits(date.getDate())}${date.getFullYear()}`;
  const timePart = `${twoDigits(date.getHours())}-${twoDigits(date.getMinutes())}-${twoDigits(date.getSeconds())}`;
  return `Retro Profiles - ${datePart}-${timePart}`;
}

export async function downloadProfilesAsZip(
  profiles: ProfileRecord[],
  fileName: string,
  onProgress?: (completed: number, total: number) => void,
): Promise<void> {
  const entries: ZipEntry[] = [];
  let completed = 0;
  let nextIndex = 0;
  let failure: Error | null = null;
  const workerCount = Math.min(6, profiles.length);

  async function worker(): Promise<void> {
    while (!failure) {
      const index = nextIndex;
      nextIndex += 1;
      if (index >= profiles.length) return;
      const profile = profiles[index];
      try {
        const response = await fetch(profile.fileUrl);
        if (!response.ok) throw new Error(`Could not download ${profile.fileName} (${response.status}).`);
        entries.push({ name: profile.relativePath, bytes: new Uint8Array(await response.arrayBuffer()), date: new Date(profile.updatedAt) });
        completed += 1;
        onProgress?.(completed, profiles.length);
      } catch (error) {
        failure = error instanceof Error ? error : new Error(`Could not download ${profile.fileName}.`);
      }
    }
  }

  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  if (failure) throw failure;
  entries.sort((left, right) => left.name.localeCompare(right.name));
  const zip = createStoredZip(entries);
  saveBlob(new Blob([zip as BlobPart], { type: 'application/zip' }), `${safeArchiveName(fileName)}.zip`);
  profiles.forEach((profile) => incrementProfileDownload(profile.id));
}

export async function downloadPackAsZip(
  pack: PackRecord,
  profiles: ProfileRecord[],
  onProgress?: (completed: number, total: number) => void,
): Promise<void> {
  await downloadProfilesAsZip(profiles, `${pack.device} - ${pack.name}`, onProgress);
  incrementPackDownload(pack.id);
}
